import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const field = "bg-[#121212] border-white/20 rounded-none text-white focus-visible:ring-1 focus-visible:ring-[#ff5a00] focus-visible:border-[#ff5a00]";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Lähetys epäonnistui. Yritä uudelleen.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="pt-32 pb-24 min-h-screen">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#ff5a00] mb-3">Yhteystiedot</p>
        <h1 className="font-display text-5xl md:text-7xl tracking-tight mb-16">Ota yhteyttä</h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div className="space-y-8">
            <div className="flex gap-4 border-b border-white/10 pb-6">
              <MapPin className="text-[#ff5a00] shrink-0" size={22} />
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">Osoite</p>
                <p className="text-white mt-1">Äijälänkatu 3, 15700 Lahti</p>
              </div>
            </div>
            {[
              { name: "Ville Paakkari", phone: "044 354 9300", tel: "0443549300", email: "ville.paakkari@lahdenautokulma.fi" },
              { name: "Teijo Paakkari", phone: "0500 653 999", tel: "0500653999", email: "teijo.paakkari@lahdenautokulma.fi" },
            ].map((s) => (
              <div key={s.email} className="border-b border-white/10 pb-6" data-testid={`seller-${s.tel}`}>
                <p className="font-mono text-[10px] uppercase tracking-widest text-[#ff5a00]">Myyjä</p>
                <p className="font-display text-2xl tracking-tight mt-1">{s.name}</p>
                <a href={`tel:${s.tel}`} className="flex gap-3 items-center mt-3 text-white hover:text-[#ff5a00] transition-colors">
                  <Phone className="text-[#ff5a00]" size={18} /> {s.phone}
                </a>
                <a href={`mailto:${s.email}`} className="flex gap-3 items-center mt-2 text-white hover:text-[#ff5a00] transition-colors break-all">
                  <Mail className="text-[#ff5a00] shrink-0" size={18} /> {s.email}
                </a>
              </div>
            ))}
            <div className="flex gap-4 border-b border-white/10 pb-6">
              <Clock className="text-[#ff5a00] shrink-0" size={22} />
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">Aukiolo</p>
                <p className="text-white mt-1">Ma–Pe 10–18 · La 10–14 · Su suljettu</p>
              </div>
            </div>
            <div className="border border-white/10 overflow-hidden">
              <iframe title="kartta" width="100%" height="260" style={{ border: 0, filter: "grayscale(1) invert(0.9)" }}
                src="https://maps.google.com/maps?q=%C3%84ij%C3%A4l%C3%A4nkatu%203,%2015700%20Lahti&z=14&output=embed" />
            </div>
          </div>

          <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
            <Input required placeholder="Nimi" value={form.name} onChange={set("name")} className={field} data-testid="contact-name" />
            <Input required type="email" placeholder="Sähköposti" value={form.email} onChange={set("email")} className={field} data-testid="contact-email" />
            <Input placeholder="Puhelin" value={form.phone} onChange={set("phone")} className={field} data-testid="contact-phone" />
            <Textarea required placeholder="Viesti" rows={6} value={form.message} onChange={set("message")} className={field} data-testid="contact-message" />
            <button type="submit" disabled={sending} data-testid="contact-submit"
              className="w-full bg-[#ff5a00] text-white font-mono text-xs uppercase tracking-widest px-8 py-4 hover:bg-white hover:text-black transition-colors duration-300 disabled:opacity-50">
              {sending ? "Lähetetään…" : "Lähetä viesti"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
